import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { 
  Table, 
  Card, 
  Form, 
  InputGroup, 
  Spinner, 
  Alert, 
  Container, 
  Pagination,
  Button,
  Modal,
  Row,
  Col
} from 'react-bootstrap';
import Layout from '../components/Layout';
import ScopeIndicator from '../components/ScopeIndicator';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { departmentAPI, calendarAPI, clearCache } from '../utils/api';
import type { Department, CreateDepartmentRequest, UpdateDepartmentRequest } from '../types/department';
import type { Calendar } from '../types/calendar';
import { formatDateTimeInCompanyTimezone, getTodayDateInCompanyTimezone } from '../utils/timezone';
import { useTranslation } from 'react-i18next';

// Lightweight icon replacements to reduce bundle size
const DepartmentIcon = () => (
  <i className="bi bi-diagram-3 me-1"></i>
);






// Memoized Department Card Component for Mobile
const DepartmentCard = memo(({ department, onEdit, canEdit, canDelete, onDelete }: {
  department: Department;
  onEdit: (department: Department) => void;
  canEdit: boolean;
  canDelete: boolean;
  onDelete: (department: Department) => void;
}) => {
  const { t } = useTranslation('pages', { keyPrefix: 'departments' });
  const { t: tc } = useTranslation('common');

  const { currentCompany } = useAuth();

  const renderCellValue = useCallback((value: any) => {
    if (value === null || value === undefined || value === '') {
      return tc('messages.na');
    }
    return value;
  }, [tc]);

  const formatDateTime = useCallback((dateString: string) => {
    return formatDateTimeInCompanyTimezone(dateString, currentCompany?.timezone || 'UTC');
  }, [currentCompany?.timezone]);

  return (
    <Card className="mobile-department-card m-3 an-data-card">
      <Card.Body className="an-data-card">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex align-items-start">
            <DepartmentIcon />
            <div>
              <h6 className="mb-1 fw-bold">{renderCellValue(department.name)}</h6>
              <small className="text-muted">{t('mobileCodePrefix')} {renderCellValue(department.department_code)}</small>
            </div>
          </div>
          {canEdit && (
            <Button variant="link" size="sm" className="text-primary p-1 me-3 text-decoration-none" onClick={() => onEdit(department)} title={t('editTooltip')}><i className="bi bi-pencil"></i> <span className="ms-1">{tc('buttons.edit')}</span></Button>
          )}
          {canDelete && (
            <Button variant="link" size="sm" className="text-danger p-1 text-decoration-none" onClick={() => onDelete(department)} title={t('deleteTooltip')}><i className="bi bi-trash"></i> <span className="ms-1">{tc('buttons.delete')}</span></Button>
          )}
        </div>
        
        <div className="mb-2">
          <small className="text-muted d-block">{t('companyId')}</small>
          <span className="fw-medium">{renderCellValue(department.company_id)}</span>
        </div>
        
        <div className="mb-2">
          <small className="text-muted d-block">{t('calendarLabel')}</small>
          <span className="fw-medium">{department.calendar_name ? department.calendar_name : t('none')}</span>
        </div>
        
        <div className="d-flex justify-content-between align-items-center">
          <small className="text-muted">
            {tc('labels.created')}: {formatDateTime(department.created_at)}
          </small>
        </div>
      </Card.Body>
    </Card>
  );
});

DepartmentCard.displayName = 'DepartmentCard';

// Memoized Table Row Component
const DepartmentTableRow = memo(({ 
  department, 
  onEdit, 
  onDelete,
  companyTimezone,
  canEdit,
  canDelete
}: { 
  department: Department; 
  onEdit: (department: Department) => void; 
  onDelete: (department: Department) => void;
  companyTimezone: string;
  canEdit: boolean;
  canDelete: boolean;
}) => {
  const { t } = useTranslation('pages', { keyPrefix: 'departments' });
  const { t: tc } = useTranslation('common');

  const renderCellValue = useCallback((value: any) => {
    if (value === null || value === undefined || value === '') {
      return tc('messages.na');
    }
    return value;
  }, [tc]);

  const formatDateTime = useCallback((dateString: string) => {
    if (!dateString) return tc('messages.na');
    return formatDateTimeInCompanyTimezone(dateString, companyTimezone);
  }, [companyTimezone, tc]);

  return (
    <tr>
      <td>
        <div className="d-flex align-items-center">
          <DepartmentIcon />
          <span className="fw-medium">{renderCellValue(department.name)}</span>
        </div>
      </td>
      <td><code className="small">{renderCellValue(department.department_code)}</code></td>
      <td><code className="small">{renderCellValue(department.company_id)}</code></td>
      <td>
        <small className="text-muted">
          {department.calendar_name ? department.calendar_name : t('none')}
        </small>
      </td>
      <td>
        <small className="text-muted">
          {formatDateTime(department.created_at)}
        </small>
      </td>
      <td>
        {canEdit && (
          <Button variant="link" size="sm" className="text-primary p-1 me-3 text-decoration-none" onClick={() => onEdit(department)} title={t('editTooltip')}><i className="bi bi-pencil"></i> <span className="ms-1">{tc('buttons.edit')}</span></Button>
        )}
        {canDelete && (
          <Button variant="link" size="sm" className="text-danger p-1 text-decoration-none" onClick={() => onDelete(department)} title={t('deleteTooltip')}><i className="bi bi-trash"></i> <span className="ms-1">{tc('buttons.delete')}</span></Button>
        )}
      </td>
    </tr>
  );
});

DepartmentTableRow.displayName = 'DepartmentTableRow';

const Departments: React.FC = () => {
  const { currentCompany } = useAuth();
  const { canAdd, canEdit, canDelete } = useRolePermissions();
  const { t } = useTranslation('pages', { keyPrefix: 'departments' });
  const { t: tc } = useTranslation('common');
  
  // Get company timezone with fallback
  const companyTimezone = currentCompany?.timezone || 'UTC';
  
  const [departments, setDepartments] = useState<Department[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingDepartment, setDeletingDepartment] = useState<Department | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalSuccess, setModalSuccess] = useState<string | null>(null);
  
  // Edit states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  
  // Additional state for calendars
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState<CreateDepartmentRequest & {calendar_id?: number, effective_from?: string}>({
    name: '',
    company_id: currentCompany?.company_id || '',
    department_code: '',
    calendar_id: undefined,
    effective_from: ''
  });

  const fetchDepartments = useCallback(async () => {
    if (!currentCompany) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await departmentAPI.getDepartments(currentCompany.company_id);
      setDepartments(response.departments || []);
    } catch (error: any) {
      console.error('Error fetching departments:', error);
      setError(error.response?.data?.message || error.message || t('fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentCompany, t]);

  const fetchCalendars = useCallback(async () => {
    if (!currentCompany) return;

    try {
      setLoadingCalendars(true);
      const response = await calendarAPI.getCalendars(currentCompany.company_id);
      setCalendars(response.calendars || []);
    } catch (error: any) {
      console.error('Error fetching calendars:', error);
      setCalendars([]);
    } finally {
      setLoadingCalendars(false);
    }
  }, [currentCompany]);



  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  useEffect(() => {
    fetchCalendars();
  }, [fetchCalendars]);

  useEffect(() => {
    setSearchTerm('');
    setPage(1);
  }, [currentCompany]);

  // Update company_id in form when currentCompany changes
  useEffect(() => {
    if (currentCompany) {
      setFormData(prev => ({ ...prev, company_id: currentCompany.company_id }));
    }
  }, [currentCompany]);

  // Memoized filtered departments to prevent unnecessary re-computations
  const filteredDepartments = useMemo(() => {
    if (!searchTerm.trim()) return departments;
    
    const searchLower = searchTerm.toLowerCase();
    return departments.filter(department => 
      department.name?.toLowerCase().includes(searchLower) ||
      department.department_code?.toLowerCase().includes(searchLower) ||
      department.company_id?.toLowerCase().includes(searchLower)
    );
  }, [departments, searchTerm]);

  const totalPages = Math.ceil(filteredDepartments.length / rowsPerPage);
  const paginatedDepartments = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredDepartments.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredDepartments, page, rowsPerPage]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleItemsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1); // Reset to first page when changing items per page
  }, []);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1); // Reset to first page when searching
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  // Modal handlers
  const handleShowModal = useCallback(() => {
    setIsEditMode(false);
    setEditingDepartment(null);
    setShowModal(true);
    setModalError(null);
    setModalSuccess(null);
    setFormData({
      name: '',
      department_code: '',
      company_id: currentCompany?.company_id || '',
      calendar_id: undefined,
      effective_from: ''
    });
  }, [currentCompany]);

  const handleEditDepartment = useCallback((department: Department) => {
    setIsEditMode(true);
    setEditingDepartment(department);
    setShowModal(true);
    setModalError(null);
    setModalSuccess(null);
    setFormData({
      name: department.name,
      department_code: department.department_code,
      company_id: department.company_id,
      calendar_id: department.calendar_id != null ? Number(department.calendar_id) : undefined,
      effective_from: getTodayDateInCompanyTimezone(companyTimezone)
    });
  }, [companyTimezone]);


    const handleDeleteDepartment = useCallback((department: Department) => {
    setDeletingDepartment(department);
    setShowDeleteModal(true);
  }, []);


  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setDeletingDepartment(null);
    setModalError(null);
    setModalSuccess(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingDepartment || !currentCompany) return;

    try {
      setModalError(null);
      setLoading(true);
      await departmentAPI.deleteDepartment(deletingDepartment.id, currentCompany.company_id);
      setModalSuccess(t('deleteSuccess'));
      await fetchDepartments();
      setTimeout(() => {
        setShowDeleteModal(false);
        setDeletingDepartment(null);
      }, 1500);
    } catch (error: any) {
      console.error('Error deleting department:', error);
      setModalError(error.response?.data?.message || error.message || t('deleteFailed'));
    } finally {
      setLoading(false);
    }
  }, [deletingDepartment, currentCompany, fetchDepartments, t]);




  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setModalError(null);
    setModalSuccess(null);
    setIsEditMode(false);
    setEditingDepartment(null);
    setFormData({
      name: '',
      department_code: '',
      company_id: currentCompany?.company_id || '',
      calendar_id: undefined,
      effective_from: ''
    });
  }, [currentCompany]);

  const handleFormChange = useCallback((field: keyof (CreateDepartmentRequest & {calendar_id?: number, effective_from?: string}), value: string | number | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentCompany) return;

    try {
      setModalLoading(true);
      setModalError(null);
      setModalSuccess(null);

      if (isEditMode && editingDepartment) {
        // Update existing department
        const updateData: UpdateDepartmentRequest = {
          name: formData.name,
          department_code: formData.department_code,
          calendar_id: formData.calendar_id,
          ...(String(formData.calendar_id ?? '') !== String(editingDepartment?.calendar_id ?? '') && formData.effective_from
            ? { effective_from: formData.effective_from }
            : {})
        };
        
        await departmentAPI.updateDepartment(
          editingDepartment.id, 
          updateData, 
          currentCompany.company_id
        );
        
        setModalSuccess(t('updateSuccess'));
      } else {
        // Create new department
        await departmentAPI.createDepartment(formData);
        setModalSuccess(t('createSuccess'));
      }
      
      // Refresh the list
      await fetchDepartments();
      
      // Close modal after a short delay
      setTimeout(() => {
        handleCloseModal();
      }, 2000);
      
    } catch (error: any) {
      console.error('Error saving department:', error);
      
      // Handle API errors
      if (error.response?.data?.message) {
        setModalError(error.response.data.message);
      } else {
        setModalError(error.message || t('saveFailed'));
      }
    } finally {
      setModalLoading(false);
    }
  }, [formData, currentCompany, fetchDepartments, handleCloseModal, isEditMode, editingDepartment, t]);

  // Generate pagination items
  const renderPaginationItems = useCallback(() => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return items;
  }, [page, totalPages, handlePageChange]);

  // Force cache clear when company changes
  useEffect(() => {
    if (currentCompany) {
      clearCache(`departments_${currentCompany.company_id}`);
    }
  }, [currentCompany]);

  return (
    <Layout title={t('title')}>
      <Container fluid className="an-enterprise-page an-page-shell an-data-page">
        <div className="page-header">
          <h2 className="an-page-title">
            {t('title')}
          </h2>
          {currentCompany && canAdd('departments') && (
            <Button variant="primary" onClick={handleShowModal}>
              <i className="bi bi-plus-lg"></i>
              {t('addDepartment')}
            </Button>
          )}
        </div>

        <ScopeIndicator resource="departments" />
        
        {currentCompany && (
          <Alert variant="info" className="mb-4">
            <strong>{tc('messages.showingFor', { entity: t('title').toLowerCase() })}</strong> {currentCompany.description} (ID: {currentCompany.company_id})
          </Alert>
        )}

        {!currentCompany && (
          <Alert variant="warning" className="mb-4">
            {tc('messages.selectCompany', { entity: t('title').toLowerCase() })}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        {/* Search Bar */}
        {currentCompany && (
          <Card className="mb-4 an-data-card">
            <Card.Body className="an-data-card">
              <InputGroup>
                <InputGroup.Text>
                  <i className="bi bi-search"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder={t('searchPlaceholder')}
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                {searchTerm && (
                  <Button variant="outline-secondary" onClick={clearSearch}>
                    <i className="bi bi-x-lg"></i>
                  </Button>
                )}
              </InputGroup>
              {searchTerm && (
                <small className="text-muted mt-2 d-block">
                  {t('found', { count: filteredDepartments.length, term: searchTerm })}
                </small>
              )}
            </Card.Body>
          </Card>
        )}

        {loading && (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" className="me-3" />
            <span>{t('loading')}</span>
          </div>
        )}

        {currentCompany && !loading && (
          <Card className="an-data-card">
            <Card.Body className="p-0 an-data-card">
              {/* Mobile Card View */}
              <div className="d-md-none">
                {paginatedDepartments.length > 0 ? (
                  paginatedDepartments.map((department) => (
                    <DepartmentCard
                      key={department.id}
                      department={department}
                      onEdit={handleEditDepartment}
                      onDelete={handleDeleteDepartment}
                      canEdit={canEdit('departments')}
                      canDelete={canDelete('departments')}
                    />
                  ))
                ) : (
                  <div className="text-center py-5">
                    <DepartmentIcon />
                    <h5>{t('noTitle')}</h5>
                    <p className="text-muted">
                      {searchTerm 
                        ? t('noMatch', { term: searchTerm, company: currentCompany.description })
                        : t('noneRegistered', { company: currentCompany.description })
                      }
                    </p>
                    {!searchTerm && canAdd('departments') && (
                      <Button variant="primary" onClick={handleShowModal}>
                        <i className="bi bi-plus-lg"></i>
                        {t('addFirstDepartment')}
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Desktop Table View */}
              <div className="d-none d-md-block">
                <div className="table-responsive an-data-table an-panel">
                  <Table hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t('departmentName')}</th>
                        <th>{t('code')}</th>
                        <th>{t('companyId')}</th>
                        <th>{t('calendarLabel')}</th>
                        <th>{tc('labels.created')}</th>
                        <th>{tc('labels.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedDepartments.length > 0 ? (
                        paginatedDepartments.map((department) => (
                          <DepartmentTableRow
                            key={department.id}
                            department={department}
                            onEdit={handleEditDepartment}
                            onDelete={handleDeleteDepartment}
                            companyTimezone={companyTimezone}
                            canEdit={canEdit('departments')}
                            canDelete={canDelete('departments')}
                          />
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="text-center py-5">
                            <DepartmentIcon />
                            <h5>{t('noTitle')}</h5>
                            <p className="text-muted mb-3">
                              {searchTerm 
                                ? t('noMatch', { term: searchTerm, company: currentCompany.description })
                                : t('noneRegistered', { company: currentCompany.description })
                              }
                            </p>
                            {!searchTerm && canAdd('departments') && (
                              <Button variant="primary" onClick={handleShowModal}>
                                <i className="bi bi-plus-lg"></i>
                                {t('addFirstDepartment')}
                              </Button>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* Pagination */}
              {filteredDepartments.length > rowsPerPage && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <span className="text-muted small">
                    {tc('messages.showing', { from: ((page - 1) * rowsPerPage) + 1, to: Math.min(page * rowsPerPage, filteredDepartments.length), total: filteredDepartments.length, entity: t('title').toLowerCase() })}
                  </span>
                  <Pagination className="mb-0 an-pagination">
                    <Pagination.Prev 
                      disabled={page === 1}
                      onClick={() => handlePageChange(page - 1)}
                    />
                    {renderPaginationItems()}
                    <Pagination.Next 
                      disabled={page === totalPages}
                      onClick={() => handlePageChange(page + 1)}
                    />
                  </Pagination>
                </div>
              )}

              {/* Items per page selector */}
              <Row className="mt-3 mb-4">
                <Col className="d-flex justify-content-center align-items-center gap-2">
                  <span className="text-muted small">{tc('messages.itemsPerPage')}</span>
                  <Form.Select
                    size="sm"
                    style={{ width: 'auto' }}
                    value={rowsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Form.Select>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {!currentCompany && !loading && (
          <Card className="an-data-card">
            <Card.Body className="text-center py-5 an-data-card">
              <h5>{tc('messages.selectCompanyTitle')}</h5>
              <p className="text-muted">
                {tc('messages.selectCompanyBody', { entity: t('title').toLowerCase() })}
              </p>
            </Card.Body>
          </Card>
        )}

        {/* Create/Edit Department Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{isEditMode ? t('editTitle') : t('addTitle')}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              {modalSuccess && (
                <Alert variant="success" className="mb-3">
                  {modalSuccess}
                </Alert>
              )}
              
              {modalError && (
                <Alert variant="danger" className="mb-3">
                  {modalError}
                </Alert>
              )}

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('nameLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      placeholder={t('namePlaceholder')}
                      required
                    />
                  </Form.Group>
                </Col>

                 <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('codeLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.department_code}
                      onChange={(e) => handleFormChange('department_code', e.target.value)}
                      placeholder={t('codePlaceholder')}
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('calendarLabel')}</Form.Label>
                    <Form.Select
                      value={formData.calendar_id || ''}
                      onChange={(e) => handleFormChange('calendar_id', e.target.value ? parseInt(e.target.value) : undefined)}
                      disabled={loadingCalendars}
                    >
                      <option value="">{t('calendarOptional')}</option>
                      {calendars.map((calendar) => (
                        <option key={calendar.id} value={calendar.id}>
                          {calendar.name}
                        </option>
                      ))}
                    </Form.Select>
                    {loadingCalendars && (
                      <small className="text-muted">{t('loadingCalendars')}</small>
                    )}
                  </Form.Group>
                </Col>

                {isEditMode && String(formData.calendar_id ?? '') !== String(editingDepartment?.calendar_id ?? '') && (
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>{t('effectiveFromLabel')}</Form.Label>
                      <Form.Control
                        type="date"
                        value={formData.effective_from}
                        onChange={(e) => handleFormChange('effective_from', e.target.value)}
                        required
                      />
                      <Form.Text className="text-muted d-block">
                        {t('effectiveFromHint')}
                        <br />
                        <small>{t('effectiveFromDefault')}</small>
                      </Form.Text>
                    </Form.Group>
                  </Col>
                )}

                <Col md={12} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('companyLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={currentCompany ? `${currentCompany.description} (${currentCompany.company_id})` : ''}
                      readOnly
                      disabled
                      className="bg-light"
                    />
                    <Form.Text className="text-muted">
                      {isEditMode 
                        ? t('companyEditHint')
                        : t('companyCreateHint')
                      }
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal} disabled={modalLoading}>
                {tc('buttons.cancel')}
              </Button>
              <Button 
                variant="primary" 
                type="submit" 
                disabled={modalLoading}
              >
                {modalLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {isEditMode ? tc('loading.updating') : tc('loading.creating')}
                  </>
                ) : (
                  <>
                    {isEditMode ? <i className="bi bi-pencil"></i> : <i className="bi bi-plus-lg"></i>}
                    {isEditMode ? t('updateDepartment') : t('createDepartment')}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Delete Department Modal */}
                <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
                  <Modal.Header closeButton className="py-2 py-md-3">
                    <Modal.Title className="h5 mb-0">{t('deleteTitle')}</Modal.Title>
                  </Modal.Header>
                  <Modal.Body className="py-2 py-md-3">
                    {modalError && (
                      <Alert variant="danger" className="mb-3">
                        {modalError}
                      </Alert>
                    )}
                    {modalSuccess && (
                      <Alert variant="success" className="mb-3">
                        {modalSuccess}
                      </Alert>
                    )}
                    
                    {deletingDepartment && (
                      <div>
                        <p className="mb-3">
                          {t('deleteConfirm', { name: deletingDepartment?.name || t('unknownName') })}
                        </p>
                        <div className="p-3 bg-light rounded">
                          <Row>
                            <Col xs={6}>
                              <small className="text-muted d-block">{t('departmentCode')}</small>
                              <strong>{deletingDepartment.department_code}</strong>
                            </Col>
                            <Col xs={6}>
                              <small className="text-muted d-block">{t('calendarLabel')}</small>
                              <strong>{deletingDepartment.calendar_name}</strong>
                            </Col>
                          </Row>
                        </div>
                        <p className="text-danger mt-3 mb-0">
                          <small>{tc('messages.cannotBeUndone')}</small>
                        </p>
                      </div>
                    )}
                  </Modal.Body>
                  <Modal.Footer className="py-2 py-md-3">
                    <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={loading}>
                      {tc('buttons.cancel')}
                    </Button>
                    <Button variant="danger" onClick={handleDeleteConfirm} disabled={loading}>
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          {tc('loading.deleting')}
                        </>
                      ) : (
                        <>
                          <i className="bi bi-trash"></i>
                          {t('deleteDepartment')}
                        </>
                      )}
                    </Button>
                  </Modal.Footer>
                </Modal>
      </Container>
    </Layout>
  );
};

export default Departments;


